const TEMPLATE_VARIABLE_REGEX = /\{\{(.+?)\}\}/g;
export const VALID_VARIABLE_NAME_REGEX = /^[A-Za-z0-9_]+$/;

export interface TemplateVariable {
  name: string;
  displayName: string;
  defaultValue?: string;
  hint?: string;
}

interface ParsedTemplateDefinition {
  name: string;
  defaultValue?: string;
  hint?: string;
}

function splitOnce(value: string, delimiter: string): [string, string | undefined] {
  const delimiterIndex = value.indexOf(delimiter);

  if (delimiterIndex === -1) {
    return [value, undefined];
  }

  return [
    value.slice(0, delimiterIndex),
    value.slice(delimiterIndex + delimiter.length),
  ];
}

function normalizeOptionalPart(value: string | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toDisplayName(name: string): string {
  return name
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function parseTemplateDefinition(definition: string): ParsedTemplateDefinition | null {
  const [rawVariableDefinition, rawHint] = splitOnce(definition, '|');
  const [rawName, rawDefaultValue] = splitOnce(rawVariableDefinition, ':');

  const name = rawName.trim();

  if (!name || !VALID_VARIABLE_NAME_REGEX.test(name)) {
    return null;
  }

  return {
    name,
    defaultValue: normalizeOptionalPart(rawDefaultValue),
    hint: normalizeOptionalPart(rawHint),
  };
}

export function parseTemplateVariables(content: string): TemplateVariable[] {
  const matches = content.matchAll(TEMPLATE_VARIABLE_REGEX);
  const seenVariables = new Set<string>();
  const variables: TemplateVariable[] = [];

  for (const match of matches) {
    const parsedDefinition = parseTemplateDefinition(match[1]);

    if (!parsedDefinition || seenVariables.has(parsedDefinition.name)) {
      continue;
    }

    variables.push({
      ...parsedDefinition,
      displayName: toDisplayName(parsedDefinition.name),
    });
    seenVariables.add(parsedDefinition.name);
  }

  return variables;
}

export function formatVariableSyntax(
  name: string,
  defaultValue?: string,
  hint?: string,
): string {
  let syntax = name;

  if (defaultValue) {
    syntax += `:${defaultValue}`;
  }

  if (hint) {
    syntax += ` | ${hint}`;
  }

  return `{{${syntax}}}`;
}

export function resolveTemplate(content: string, values: Record<string, string>): string {
  return content.replace(TEMPLATE_VARIABLE_REGEX, (fullMatch, definition) => {
    const parsedDefinition = parseTemplateDefinition(definition);

    if (!parsedDefinition) {
      return fullMatch;
    }

    if (Object.prototype.hasOwnProperty.call(values, parsedDefinition.name)) {
      return values[parsedDefinition.name];
    }

    return fullMatch;
  });
}
